import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin, Flame } from "lucide-react";

const RevealLine = ({ children, delay = 0, className = "" }) => (
    <span className={`block overflow-hidden pb-1 ${className}`}>
        <motion.span
            className="block will-change-transform"
            initial={{ y: "115%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

export default function KineticHero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 130]);
    const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
    const textY = useTransform(scrollYProgress, [0, 1], [0, -80]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section
            id="hero"
            ref={ref}
            data-testid="hero-section"
            className="relative flex min-h-screen items-center overflow-hidden pt-28 pb-16"
        >
            <div className="pointer-events-none absolute -left-40 top-1/3 h-[480px] w-[480px] rounded-full bg-rose-600/20 blur-[140px]" />
            <div className="pointer-events-none absolute -right-32 bottom-0 h-[380px] w-[380px] rounded-full bg-rose-700/15 blur-[120px]" />
            <div
                className="text-stroke font-display pointer-events-none absolute bottom-4 left-0 select-none whitespace-nowrap text-[16vw] font-black uppercase leading-none opacity-40"
                aria-hidden
            >
                Petit Veau · Feu de Bois
            </div>

            <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
                <motion.div style={{ y: textY, opacity: fade }} className="lg:col-span-7">
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.3 }}
                        className="font-mono2 mb-6 flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-rose-400 sm:text-xs"
                    >
                        <Flame className="h-4 w-4" />
                        Restaurant — Cuit à la Braise — Athis-Mons
                    </motion.p>

                    <h1
                        data-testid="hero-headline"
                        className="font-display text-5xl font-black uppercase leading-[0.95] tracking-tight sm:text-7xl lg:text-[5.4rem]"
                    >
                        <RevealLine delay={0.35}>Petit Veau</RevealLine>
                        <RevealLine delay={0.5} className="normal-case">
                            <span className="font-script text-rose-500">Athis-Mons</span>
                        </RevealLine>
                        <RevealLine delay={0.65} className="text-3xl sm:text-5xl lg:text-6xl">
                            Le goût du feu de bois.
                        </RevealLine>
                    </h1>

                    <motion.p
                        data-testid="hero-subtitle"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.9 }}
                        className="mt-7 max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg"
                    >
                        Des produits frais, des recettes savoureuses et un goût unique !
                        Sandwichs, assiettes, fantastiques et burgers cuits à la braise,
                        au cœur d'Athis-Mons.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1.05 }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <motion.a
                            href="#menu"
                            data-testid="hero-primary-cta"
                            whileHover={{ scale: 1.04 }}
                            whileTap={{ scale: 0.97 }}
                            className="font-mono2 inline-flex items-center gap-2 rounded-full bg-rose-600 px-7 py-4 text-xs font-bold uppercase tracking-widest text-white transition-colors duration-300 hover:bg-rose-500 glow-red"
                        >
                            Voir le Menu
                            <ArrowDown className="h-4 w-4" />
                        </motion.a>
                        <motion.a
                            href="#location"
                            data-testid="hero-secondary-cta"
                            whileHover={{ scale: 1.04 }}
                            whileTap={{ scale: 0.97 }}
                            className="font-mono2 inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-4 text-xs font-bold uppercase tracking-widest text-zinc-200 transition-colors duration-300 hover:border-rose-500/60 hover:text-rose-400"
                        >
                            <MapPin className="h-4 w-4" />
                            Nous Trouver
                        </motion.a>
                    </motion.div>
                </motion.div>

                <div className="relative lg:col-span-5">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, rotate: 3 }}
                        animate={{ opacity: 1, scale: 1, rotate: 0 }}
                        transition={{ duration: 1.1, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
                        className="animate-float-slow relative"
                    >
                        <div
                            data-testid="hero-image-spotlight"
                            className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-white/10"
                        >
                            <motion.img
                                style={{ y: imgY, scale: imgScale }}
                                src="/assets/storefront.jpg"
                                alt="Restaurant Petit Veau Athis-Mons — devanture"
                                className="h-full w-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/85 via-transparent to-transparent" />
                            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                                <div>
                                    <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-rose-400">
                                        Notre Adresse
                                    </p>
                                    <p className="font-display mt-1 text-xl font-black uppercase leading-tight">
                                        180 Av. François Mitterrand
                                    </p>
                                </div>
                                <p className="font-script shrink-0 text-2xl text-rose-500">
                                    91200
                                </p>
                            </div>
                        </div>
                        <div className="font-mono2 absolute -left-4 top-6 rounded-full border border-white/10 bg-[#121212]/90 px-4 py-2 text-[10px] uppercase tracking-[0.25em] text-zinc-300 backdrop-blur-md">
                            Cuit à la Braise
                        </div>
                        <div className="font-mono2 absolute -right-3 bottom-24 rounded-full border border-rose-500/40 bg-rose-600/20 px-4 py-2 text-[10px] uppercase tracking-[0.25em] text-rose-300 backdrop-blur-md">
                            Ouvert 6j/7
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
